<template>
  <div class="matched-items-progress">
    <div class="progress-labels d-flex justify-space-between mb-1">
      <span class="text-caption">{{ linkedCount }} linked</span>
      <span v-if="multipleMatchesCount > 0" class="text-caption">{{ multipleMatchesCount }} pending</span>
      <span class="text-caption">{{ matchedCount }} matched</span>
      <span class="text-caption">{{ totalSubtasks }} total</span>
    </div>
    <v-progress-linear
      :model-value="100"
      height="24"
      rounded
      bg-color="grey-lighten-2"
    >
      <template #default>
        <div class="progress-segments">
          <div
            class="segment linked"
            :style="{ width: linkedPercentage + '%' }"
          >
            <span v-if="linkedPercentage > 10">{{ linkedCount }}</span>
          </div>
          <div
            v-if="multipleMatchesCount > 0"
            class="segment multiple-matches"
            :style="{ width: multipleMatchesPercentage + '%' }"
          >
            <span v-if="multipleMatchesPercentage > 10">{{ multipleMatchesCount }}</span>
          </div>
          <div
            class="segment matched"
            :style="{ width: matchedPercentage + '%' }"
          >
            <span v-if="matchedPercentage > 10">{{ matchedCount }}</span>
          </div>
          <div
            class="segment unmatched"
            :style="{ width: unmatchedPercentage + '%' }"
          >
            <span v-if="unmatchedPercentage > 10">{{ unmatchedCount }}</span>
          </div>
        </div>
      </template>
    </v-progress-linear>
    <div class="legend d-flex justify-center ga-4 mt-2">
      <div class="legend-item d-flex align-center">
        <div class="legend-color linked"></div>
        <span class="text-caption ml-1">Linked: {{ linkedCount }}</span>
      </div>
      <div v-if="multipleMatchesCount > 0" class="legend-item d-flex align-center">
        <div class="legend-color multiple-matches"></div>
        <span class="text-caption ml-1">Pending: {{ multipleMatchesCount }}</span>
      </div>
      <div class="legend-item d-flex align-center">
        <div class="legend-color matched"></div>
        <span class="text-caption ml-1">Matched: {{ matchedCount }}</span>
      </div>
      <div class="legend-item d-flex align-center">
        <div class="legend-color unmatched"></div>
        <span class="text-caption ml-1">Unmatched: {{ unmatchedCount }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  linkedCount: {
    type: Number,
    default: 0
  },
  multipleMatchesCount: {
    type: Number,
    default: 0
  },
  matchedCount: {
    type: Number,
    default: 0
  },
  totalSubtasks: {
    type: Number,
    default: 0
  }
});

const unmatchedCount = computed(() => {
  return Math.max(0, props.totalSubtasks - props.linkedCount - props.multipleMatchesCount - props.matchedCount);
});

const linkedPercentage = computed(() => {
  if (props.totalSubtasks === 0) return 0;
  return (props.linkedCount / props.totalSubtasks) * 100;
});

const multipleMatchesPercentage = computed(() => {
  if (props.totalSubtasks === 0) return 0;
  return (props.multipleMatchesCount / props.totalSubtasks) * 100;
});

const matchedPercentage = computed(() => {
  if (props.totalSubtasks === 0) return 0;
  return (props.matchedCount / props.totalSubtasks) * 100;
});

const unmatchedPercentage = computed(() => {
  if (props.totalSubtasks === 0) return 100;
  return (unmatchedCount.value / props.totalSubtasks) * 100;
});
</script>

<style scoped>
.matched-items-progress {
  width: 100%;
}

.progress-segments {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 4px;
}

.segment {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 500;
  transition: width 0.3s ease;
}

.segment.linked {
  background-color: #4caf50;
}

.segment.multiple-matches {
  background-color: #ff9800;
}

.segment.matched {
  background-color: #2196f3;
}

.segment.unmatched {
  background-color: #9e9e9e;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-color.linked {
  background-color: #4caf50;
}

.legend-color.multiple-matches {
  background-color: #ff9800;
}

.legend-color.matched {
  background-color: #2196f3;
}

.legend-color.unmatched {
  background-color: #9e9e9e;
}
</style>
